import { useEffect, useState } from "react"
import { useGetVirasafeMetricsQuery } from "../generated/graphql"
import { VirasafeMetrics } from "../utils/types"
import { useGraphQLClient } from "./useGraphQLClient"

/**
 * Custom React hook to fetch and manage Virasafe metrics data.
 *
 * This hook retrieves metrics data from the `RealmVirasafeMetrics` model and provides
 * the metrics along with computed counts for top engaged activities and least distressed activities.
 * It uses React's `useState` and `useEffect` to manage state and side effects.
 *
 * @returns {Object} An object containing:
 * - `metrics` (RealmVirasafeMetrics | null): The fetched metrics data or `null` if not available.
 * - `numTopEngagedActivities` (number | null): The count of top engaged activities, or `null` if metrics are not available.
 * - `numLeastDistressedActivities` (number | null): The count of least distressed activities, or `null` if metrics are not available.
 *
 * @example
 * const { metrics, numTopEngagedActivities, numLeastDistressedActivities } = useVirasafeMetrics();
 * console.log(metrics, numTopEngagedActivities, numLeastDistressedActivities);
 */
export const useVirasafeMetrics = (clientId: string) => {
  const [metrics, setMetrics] = useState<VirasafeMetrics | null>(null)
  const [numTopEngagedActivities, setNumTopEngagedActivities] = useState<
    number | null
  >(null)
  const [numLeastDistressedActivities, setNumLeastDistressedActivities] =
    useState<number | null>(null)

  const graphqlClient = useGraphQLClient()
  const metricsData = useGetVirasafeMetricsQuery(graphqlClient)

  useEffect(() => {
    const metrics =
      metricsData.data?.vw_user_virasafe_metrics.find(
        (m) => m.user_id === clientId
      ) || null
    if (!metrics) return
    setMetrics(metrics)

    let topEngagedCount = 0
    if (metrics) {
      if (metrics.top_engaged_activity_1) topEngagedCount++
      if (metrics.top_engaged_activity_2) topEngagedCount++
      if (metrics.top_engaged_activity_3) topEngagedCount++
      if (metrics.top_engaged_activity_4) topEngagedCount++
      setNumTopEngagedActivities(topEngagedCount)

      let leastDistressedCount = 0
      if (metrics.least_distressed_activity_1) leastDistressedCount++
      if (metrics.least_distressed_activity_2) leastDistressedCount++
      if (metrics.least_distressed_activity_3) leastDistressedCount++
      setNumLeastDistressedActivities(leastDistressedCount)
    }
  }, [metricsData])
  return { metrics, numTopEngagedActivities, numLeastDistressedActivities }
}
